import {
  formatMaltaMoneyValue,
  parseMaltaMoneyValue,
} from "@/src/utils/admissionOffice/malta/buildMaltaFullTimeAdmissionBody";

export const SEMESTER_COST_API_KEYS = [
  "cost_first_semester",
  "cost_second_semester",
  "cost_third_semester",
  "cost_fourth_semester",
  "cost_fifth_semester",
  "cost_sixth_semester",
  "cost_seventh_semester",
  "cost_eighth_semester",
  "cost_ninth_semester",
  "cost_tenth_semester",
  "cost_eleventh_semester",
  "cost_twelfth_semester",
] as const;

export type SemesterCostApiKey = (typeof SEMESTER_COST_API_KEYS)[number];

export type SemesterCostFields = Record<SemesterCostApiKey, string>;

export interface SemesterCostTaskSource {
  admission_semester?: string | null;
  duration?: string | null;
  tuition_fee?: string | null;
  tuition_fee_flag?: boolean | string | number | null;
}

const ORDINAL_TO_INDEX: Record<string, number> = {
  "1st": 1,
  first: 1,
  "2nd": 2,
  second: 2,
  "3rd": 3,
  third: 3,
  "4th": 4,
  fourth: 4,
  "5th": 5,
  fifth: 5,
  "6th": 6,
  sixth: 6,
  "7th": 7,
  seventh: 7,
  "8th": 8,
  eighth: 8,
  "9th": 9,
  ninth: 9,
  "10th": 10,
  tenth: 10,
  "11th": 11,
  eleventh: 11,
  "12th": 12,
  twelfth: 12,
};

/**
 * Parses "4th Semester" / "4th semester" / "4th-semester" → 1..12.
 * Returns null for PG/PhD or unrecognized values.
 */
export const parseAdmissionSemesterIndex = (value?: string | null): number | null => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (/pg\s*\/?\s*phd/i.test(trimmed)) return null;

  const match = trimmed.match(/\b(\d{1,2})(st|nd|rd|th)\b/i) ?? trimmed.match(/\b(first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth)\b/i);
  if (!match) return null;

  const token = match[1].toLowerCase();
  const fromOrdinal = ORDINAL_TO_INDEX[token] ?? ORDINAL_TO_INDEX[`${token}${match[2]?.toLowerCase() ?? ""}`];
  if (fromOrdinal != null) return fromOrdinal;

  const numeric = Number.parseInt(token, 10);
  if (!Number.isFinite(numeric) || numeric < 1 || numeric > 12) return null;
  return numeric;
};

/** "4,5 years" / "4.5 years" / "6 years" → years as float. */
export const parseDurationYears = (value?: string | null): number | null => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;

  const match = trimmed.replace(/\s+/g, " ").match(/(\d+(?:[.,]\d+)?)/);
  if (!match) return null;

  const years = Number.parseFloat(match[1].replace(",", "."));
  return Number.isFinite(years) && years > 0 ? years : null;
};

export const parseTuitionFeeFlag = (value?: boolean | string | number | null): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1";
  }
  return false;
};

/**
 * Builds Cost of the 1st..12th semester from task fields.
 *
 * - Start index = admission_semester (e.g. "4th Semester" → fill from 4)
 * - Count = duration years × 2 (e.g. 4.5 → 9 semesters)
 * - Amount = tuition_fee if tuition_fee_flag, else tuition_fee / 2
 * - Slots before start and after the duration window are "0"
 */
export const buildSemesterCostsFromTask = (
  source: SemesterCostTaskSource,
): SemesterCostFields | null => {
  const startIndex = parseAdmissionSemesterIndex(source.admission_semester);
  const durationYears = parseDurationYears(source.duration);
  const tuitionAmount = parseMaltaMoneyValue(source.tuition_fee ?? undefined);

  if (startIndex == null || durationYears == null || tuitionAmount <= 0) {
    return null;
  }

  const semesterCount = Math.round(durationYears * 2);
  if (semesterCount <= 0) return null;

  const isPerSemester = parseTuitionFeeFlag(source.tuition_fee_flag);
  const semesterPrice = isPerSemester ? tuitionAmount : tuitionAmount / 2;
  const priceLabel = formatMaltaMoneyValue(semesterPrice) || "0";
  const endIndex = Math.min(12, startIndex + semesterCount - 1);

  const result = {} as SemesterCostFields;
  SEMESTER_COST_API_KEYS.forEach((key, offset) => {
    const semesterNumber = offset + 1;
    const inPaidRange = semesterNumber >= startIndex && semesterNumber <= endIndex;
    result[key] = inPaidRange ? priceLabel : "0";
  });

  return result;
};
